import { useRef, useEffect } from "react"
import type { ILicenseType } from "../clients/config"
import type { ISelectedLicenceType } from "../data"
import { clearErrorsForInput } from "./registration_form_component"
import { Tooltip } from "react-tooltip"

/**
 * Briefly allows re-adding a second license if mistakenly removed on an old form.
 */
let wasMultiLicenseForm = false

export interface ILicenseTypeMultiSelectProps {
  available_license_types: ILicenseType[]
  selected_licenses_types: ISelectedLicenceType[]
  max_selected_licenses_types: number | null
  show_licensed_entity_name: boolean
  license_type_field_label: string
  license_type_tooltip?: string
  required: boolean
  onAddLicenseType: () => void
  onRemoveLicenseType: (index: number) => void
  onChange: (index: number, key: keyof ISelectedLicenceType, value: string) => void
}
export function LicenseTypeMultiSelect({
  available_license_types,
  selected_licenses_types,
  max_selected_licenses_types,
  show_licensed_entity_name,
  license_type_field_label,
  license_type_tooltip,
  required,
  onAddLicenseType,
  onRemoveLicenseType,
  onChange,
}: ILicenseTypeMultiSelectProps) {
  useEffect(() => {
    if (selected_licenses_types.length > 1) {
      wasMultiLicenseForm = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const nestedFormsToRender = selected_licenses_types.length > 0 ? selected_licenses_types : [{}]

  const maxSelectedLicenseTypes = max_selected_licenses_types ?? 10

  const addAnotherEnabled = ((wasMultiLicenseForm || selected_licenses_types.length < maxSelectedLicenseTypes) &&
    ((selected_licenses_types.length === 1 && selected_licenses_types[0]?.name) || (selected_licenses_types.length > 1)))

  return (<>
    {nestedFormsToRender.map((selected_licenses_type, index) => {
      return (<div key={`single_license_form_${index}`}>
        <SingleLicenseForm
          index={index}
          show_index={selected_licenses_types.length > 1}
          required={required}
          available_license_types={available_license_types}
          selected_licenses_type={selected_licenses_type}
          show_licensed_entity_name={show_licensed_entity_name}
          license_type_field_label={license_type_field_label}
          license_type_tooltip={license_type_tooltip}
          onChange={onChange}
        />
        {index < (selected_licenses_types.length - 1) && selected_licenses_types.length > 1 && (
          <div className="row">
            <div className="col-sm-9 col-sm-offset-3">
              <hr style={{margin: "5px 0 15px 0"}} />
            </div>
          </div>
        )}
      </div>)
    })}
    <div className="form-group hidden-print">
      {addAnotherEnabled && (<div className="col-sm-4 col-sm-offset-3">
        <p className="add-another" onClick={onAddLicenseType} >
          <i className="fa fa-plus-circle fa-lg add-another text-success" />&nbsp;&nbsp;Add Another {license_type_field_label}
        </p>
      </div>)}
      {selected_licenses_types.length > 1 && (
        <div className={`col-sm-5 hidden-print${addAnotherEnabled ? "" : " col-sm-offset-3"}`}>
          <p className="add-another" onClick={() => onRemoveLicenseType(selected_licenses_types.length - 1)}>
            <span className="fa fa-minus-circle fa-lg text-danger remove-this" />&nbsp;&nbsp;Remove {license_type_field_label}
          </p>
        </div>
      )}
      <div className="col-sm-12">
        <hr style={{ margin: "5px 0" }} />
      </div>
    </div>
  </>)
}

interface ISingleLicenseFormProps {
  index: number
  show_index: boolean
  required: boolean
  available_license_types: ILicenseType[]
  selected_licenses_type: ISelectedLicenceType
  show_licensed_entity_name: boolean
  license_type_field_label: string
  license_type_tooltip?: string
  onChange: (index: number, key: keyof ISelectedLicenceType, value: string) => void
}
function SingleLicenseForm({
  index,
  show_index,
  required,
  available_license_types,
  selected_licenses_type,
  show_licensed_entity_name,
  license_type_field_label,
  license_type_tooltip,
  onChange,
}: ISingleLicenseFormProps) {
  const licenseType = selected_licenses_type.name && available_license_types.find((type) => type.name === selected_licenses_type.name)

  const customNameInput = useRef<HTMLInputElement>(null)
  const lincensedEntityNameInput = useRef<HTMLInputElement>(null)
  const licenseNumberInput = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (licenseType) {
      if (licenseType.require_custom_name && !selected_licenses_type.custom_name) {
        customNameInput.current?.focus()
      } else if (!selected_licenses_type.licensed_entity_name) {
        lincensedEntityNameInput.current?.focus()
      }
    }

    // Clear any error messages for the license number
    if (licenseNumberInput.current) {
      clearErrorsForInput(licenseNumberInput.current)
    }
    if (customNameInput.current) {
      clearErrorsForInput(customNameInput.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenseType])

  return (<>
    <div className="form-group">
      <label
        className={`col-sm-3 control-label${required ? " required" : ""}`}
        htmlFor={`insurer_license_type_${index}_name`}
      >
        {license_type_field_label} {show_index && (index + 1)} &nbsp;
        {license_type_tooltip && (
          <>
            <i className="fa fa-question-circle"
              data-tooltip-id={`tip-license-type-${index}`}
              data-tooltip-content={license_type_tooltip}
            />
            <Tooltip id={`tip-license-type-${index}`} className="registration-tooltip" />
          </>
        )}
      </label>
      <div className="col-sm-4">
        <select
          id={`insurer_license_type_${index}_name`}
          name={`insurer[license_types][${index}][name]`}
          value={selected_licenses_type.name || ""}
          onChange={(event) => onChange(index, "name", event.currentTarget.value)}
          className="form-control"
          required={required}
          data-fv-notempty-message={`Please select a ${license_type_field_label}`}
        >
          <option/>
          {available_license_types.map((type) => {
            return (
              <option key={type.name} value={type.name}>{type.label}</option>
            )
          })}
        </select>
      </div>
      {licenseType?.require_custom_name && (
        <div className="col-sm-5">
          <input
            ref={customNameInput}
            onChange={(event) => onChange(index, "custom_name", event.currentTarget.value)}
            name={`insurer[license_types][${index}][custom_name]`}
            value={selected_licenses_type.custom_name || ""}
            className="form-control"
            required={licenseType.require_custom_name}
            placeholder={`Specify ${license_type_field_label}${licenseType.require_custom_name ? " *" : ""}`}
            aria-label={`Specify ${license_type_field_label}${licenseType.require_custom_name ? " (Required)" : ""}`}
            id={`insurer_license_type_${index}_custom_name`}
            maxLength={100}
            data-fv-notempty-message="Please Specify"
          />
        </div>
      )}
    </div>

    {licenseType && <>
      {show_licensed_entity_name && (
        <div className="form-group">
          <div className="col-sm-9 col-sm-offset-3">
            <input
              ref={lincensedEntityNameInput}
              onChange={(event) => onChange(index, "licensed_entity_name", event.currentTarget.value)}
              name={`insurer[license_types][${index}][licensed_entity_name]`}
              value={selected_licenses_type.licensed_entity_name || ""}
              className="form-control"
              required={true}
              placeholder="Licensed Entity Name"
              aria-label="Licensed Entity Name"
              id={`insurer_license_type_${index}_license_entity_name`}
              maxLength={100}
            />
          </div>
        </div>
      )}
      <div className="form-group">
        <div className="col-sm-9 col-sm-offset-3">
          <input
            ref={licenseNumberInput}
            onChange={(event) => onChange(index, "license_number", event.currentTarget.value)}
            name={`insurer[license_types][${index}][license_number]`}
            value={selected_licenses_type.license_number || ""}
            className="form-control"
            required={licenseType.require_license_number}
            placeholder={`License Number${licenseType.require_license_number ? " *" : ""}`}
            aria-label={`License Number${licenseType.require_license_number ? " (Required)" : ""}`}
            id={`insurer_license_type_${index}_license_number`}
            maxLength={100}
            disabled={!licenseType.show_license_number}
            data-fv-notempty-message={"Please enter a License Number"}
          />
        </div>
      </div>
    </>}
  </>)
}
